// Copyright 2025, University of Maryland, All Rights Reserved
import '@universityofmaryland/utilityheader';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, Navigate, Outlet, useSearchParams } from "react-router-dom";
import { Menu } from "lucide-react";
import { toast } from 'sonner';
import {
  OrganizationSelector,
  WorkspaceSelector,
  ImpersonationBanner,
  ProfileMenu,
  WorkspaceWizard,
  useAuth,
} from '@/subsystems/workspaces';
import { DynamicTabs, useWorkspaceConfig } from '@/core';
import { SiteBanner } from '@/subsystems/site-banner';
import { getWorkspaceVaultPairing } from '@/subsystems/vault/services/vault-api';
import { useServices } from '@/app-shell/services';
import { AuthMenuSync } from './AuthMenuSync';
import Sidebar from './Sidebar';
import { SidebarProvider, SidebarToggle } from './SidebarContext';

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const api = useServices().getClient();
  const {
    isGlobalAdmin,
    selectedOrganizationId,
    selectOrganization,
    selectedWorkspaceId,
    selectWorkspace,
    isAuthenticated,
    accessibleOrganizations = [],
    accessibleWorkspaces,
    currentWorkspace,
    canCreateWorkspace,
    isWorkspaceAdmin,
    loadingWorkspaces,
  } = useAuth();

  // Show organization selector if user is admin OR has access to multiple organizations
  const showOrgSelector = isGlobalAdmin || accessibleOrganizations.length > 1;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);
  const [newAgentId, setNewAgentId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTwoRowLayout, setIsTwoRowLayout] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTabId = searchParams.get('tab') ?? '';
  const urlOrg = searchParams.get('org');
  const urlWs = searchParams.get('ws');
  const deepLinkApplied = useRef(false);
  // cm-coding: Paired-Vault discovery is asynchronous. Only the latest global
  // workspace choice may complete navigation, or a slower earlier response
  // could reopen the wrong project's Vault after a rapid second selection.
  const workspaceSelectionVersion = useRef(0);
  const [workspaceSelectorRefreshKey, setWorkspaceSelectorRefreshKey] = useState(0);
  const [appSelectorOpen, setAppSelectorOpen] = useState(false);

  // Deep link: if URL has org+ws params, select that workspace on mount
  // Only apply if the user actually has access to the workspace (prevents 403 after
  // impersonation, stale bookmarks, or shared links to workspaces the user can't access).
  // When the workspace is inaccessible, redirect to the dashboard so we don't silently
  // swap their workspace under them on a deep-linked route.
  // Wait until accessibleWorkspaces has loaded for the URL's org before deciding —
  // accessibleWorkspaces only contains the currently-selected org's workspaces, so a
  // mismatched urlOrg would otherwise look "inaccessible" even when the user has access.
  useEffect(() => {
    if (deepLinkApplied.current) return;
    if (!urlOrg || !urlWs) return;
    if (loadingWorkspaces) return;
    if (selectedOrganizationId !== urlOrg) {
      // Don't call selectOrganization for an org the user can't access — the
      // workspaces fetch would 403, and QueryClient.throwOnError blanks the tree.
      const orgAccessible = accessibleOrganizations.some(o => o.orgId === urlOrg);
      if (!orgAccessible) {
        deepLinkApplied.current = true;
        console.log(`[Layout] Deep link org ${urlOrg} not accessible — redirecting to dashboard`);
        navigate('/', { replace: true });
        return;
      }
      selectOrganization(urlOrg);
      return;
    }
    deepLinkApplied.current = true;
    const hasAccess = accessibleWorkspaces.some(w => w.orgId === urlOrg && w.wsId === urlWs);
    if (hasAccess && urlWs !== selectedWorkspaceId) {
      selectWorkspace(urlOrg, urlWs);
    } else if (!hasAccess) {
      console.log(`[Layout] Deep link workspace ${urlOrg}/${urlWs} not accessible — redirecting to dashboard`);
      navigate('/', { replace: true });
    }
  }, [urlOrg, urlWs, selectedOrganizationId, selectedWorkspaceId, selectOrganization, selectWorkspace, accessibleOrganizations, accessibleWorkspaces, loadingWorkspaces, navigate]);

  // Sync org/ws into URL so every URL is shareable
  useEffect(() => {
    if (selectedOrganizationId && selectedWorkspaceId) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        const workspaceChanged = next.get('org') !== selectedOrganizationId
          || next.get('ws') !== selectedWorkspaceId;
        if (!workspaceChanged) {
          return prev;
        }
        next.set('org', selectedOrganizationId);
        next.set('ws', selectedWorkspaceId);
        // cm-coding: Organization changes and automatic workspace selection
        // bypass the workspace selector callback. Clear the prior Vault route
        // here so its documents cannot remain visible under the new workspace.
        if (location.pathname === '/vault') {
          next.delete('vault');
          next.delete('tab');
        }
        return next;
      }, { replace: true });
    }
  }, [location.pathname, selectedOrganizationId, selectedWorkspaceId, setSearchParams]);

  const handleCreateWorkspace = useCallback(() => {
    setAppSelectorOpen(true);
  }, []);

  const handleWorkspaceSelect = useCallback((orgId: string, wsId: string) => {
    const selectionVersion = ++workspaceSelectionVersion.current;
    const isVaultRoute = location.pathname === '/vault';
    // cm-coding: The Vault page must show documents for the workspace named in
    // the global selector. Clear the prior Vault ID as part of a workspace
    // switch, then resolve the selected workspace's pairing; retaining the old
    // ID can expose one workspace's documents under another workspace's header.
    if (isVaultRoute) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('org', orgId);
        next.set('ws', wsId);
        next.delete('vault');
        next.delete('tab');
        return next;
      }, { replace: true });
    }
    selectWorkspace(orgId, wsId);
    if (isVaultRoute) {
      void getWorkspaceVaultPairing(api, orgId, wsId)
        .then(pairing => {
          if (selectionVersion !== workspaceSelectionVersion.current) return;
          setSearchParams(current => {
            if (!pairing) return current;
            const next = new URLSearchParams(current);
            next.set('org', orgId);
            next.set('ws', wsId);
            next.set('vault', pairing.vaultId);
            return next;
          }, { replace: true });
        })
        .catch(() => toast.error('Workspace selected, but its Vault could not be loaded.'));
    }
  }, [api, location.pathname, selectWorkspace, setSearchParams]);

  const handleWorkspaceCreated = useCallback((orgId: string, wsId: string) => {
    setAppSelectorOpen(false);
    selectWorkspace(orgId, wsId);
    setWorkspaceSelectorRefreshKey((prev) => prev + 1);
  }, [selectWorkspace]);

  // Handle tab changes - navigate to dashboard if clicking a tab while on a route page
  const handleTabChange = useCallback((tabId: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (tabId) {
        next.set('tab', tabId);
      } else {
        next.delete('tab');
      }
      return next;
    }, { replace: true });
    if (location.pathname !== '/') {
      navigate('/');
    }
  }, [setSearchParams, location.pathname, navigate]);

  // Load workspace configuration (fast - no DB queries)
  const {
    layout,
    loading: configLoading,
    error: configError,
  } = useWorkspaceConfig(
    selectedOrganizationId || undefined,
    selectedWorkspaceId || undefined,
    currentWorkspace?.role !== 'connector-admin',
  );

  // Detect 403 on workspace config — user doesn't have access to this workspace
  // (e.g., deep link after impersonation, stale bookmark, shared URL)
  const is403 = useMemo(() => {
    if (!configError) return false;
    const err = configError as Error & { response?: { status?: number } };
    return err.response?.status === 403;
  }, [configError]);

  // Auto-fallback: if workspace config returns 403, redirect to the dashboard and switch
  // to the first accessible workspace in the currently-selected org. Wait for an in-flight
  // org switch to settle so we don't fall back using a stale accessibleWorkspaces list,
  // which would flip the org back and fight the user's selection.
  useEffect(() => {
    if (!is403) return;
    if (loadingWorkspaces) return;
    const fallback = accessibleWorkspaces.find(w => w.orgId === selectedOrganizationId);
    if (fallback && fallback.wsId !== selectedWorkspaceId) {
      console.log(`[Layout] 403 on workspace config — falling back to accessible workspace: ${fallback.orgId}/${fallback.wsId}`);
      selectWorkspace(fallback.orgId, fallback.wsId);
    }
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [is403, loadingWorkspaces, accessibleWorkspaces, selectedOrganizationId, selectedWorkspaceId, selectWorkspace, location.pathname, navigate]);

  // Derive user role from isGlobalAdmin
  // isGlobalAdmin true means global admin in this context
  const userRole = isGlobalAdmin ? 'global_admin' : 'workspace_user';

  useEffect(() => {
    // Function to check if we're on a mobile device and if we need two-row layout
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      // Increased breakpoint to 1536px to handle remote desktop on iPad and larger viewports
      // Remote desktop can report larger dimensions, so we need a higher threshold
      setIsTwoRowLayout(window.innerWidth < 1536 && window.innerWidth >= 640);
    };

    // Check initially
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);

    // Clean up
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Expose the open sidebar width on document root so portaled overlays
  // (e.g. FullscreenDialog) can offset to the right of the sidebar instead
  // of covering it.
  useEffect(() => {
    const width = isAuthenticated && leftSidebarOpen ? '16rem' : '0px'
    document.documentElement.style.setProperty('--app-sidebar-width', width)
    return () => {
      document.documentElement.style.removeProperty('--app-sidebar-width')
    }
  }, [isAuthenticated, leftSidebarOpen])

  return (
    <SidebarProvider value={{ isOpen: leftSidebarOpen, setOpen: setLeftSidebarOpen, newAgentId, setNewAgentId }}>
    <div className="h-screen flex flex-col bg-gray-100 overflow-hidden">
      <umd-utility-header
        size="100%"
        padding="20px"
      ></umd-utility-header>
      <AuthMenuSync menuOpen={mobileMenuOpen} />
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {isAuthenticated && (
          <Sidebar
            open={leftSidebarOpen}
            onClose={() => setLeftSidebarOpen(false)}
            isGlobalAdmin={isGlobalAdmin}
            isWorkspaceAdmin={isWorkspaceAdmin}
          />
        )}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Site + impersonation banners - below utility header, above main nav */}
        <SiteBanner />
        <ImpersonationBanner app_id="docreview" />
        <nav className="bg-white shadow-sm flex-shrink-0 relative">
            {isAuthenticated && !isMobile && (
              <SidebarToggle className="absolute left-4 top-2.5 z-10 h-11 w-11 rounded-lg border border-slate-200 text-slate-700 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2" />
            )}
            {/* cm-coding: Workspace and application selectors are the user's
                navigation context and must remain operable when the sidebar is
                collapsed. The absolute toggle owns the first 4rem in both the
                one- and two-row header layouts; preserving that gutter prevents
                the control from obscuring the active-context selectors. */}
            <div className={`container mx-auto px-4 ${isAuthenticated && !isMobile ? 'pl-20' : ''}`}>
              <div className="flex h-16 items-center justify-between">
                {isMobile ? (
                  // Mobile view - show hamburger menu
                  <>
                    <button
                      type="button"
                      className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100"
                      onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                      aria-expanded={mobileMenuOpen}
                      aria-label="Toggle navigation menu"
                    >
                      <Menu className="h-6 w-6" aria-hidden="true" />
                    </button>
                    <div className="flex-1 text-center">
                      <span className="text-sm font-medium text-gray-700">Docreview</span>
                    </div>
                  </>
                ) : isTwoRowLayout ? (
                  // Two-row layout (width < 1536px but not mobile)
                  <>
                    <div className="flex items-center space-x-2">
                      {/* Show organization selector for global admins or users with multiple orgs */}
                      {isAuthenticated && showOrgSelector && (
                        <OrganizationSelector
                          app_id="docreview"
                          selectedOrganizationId={selectedOrganizationId || undefined}
                          onOrganizationSelect={selectOrganization}
                          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                        />
                      )}
                      {/* Only show workspace switcher when authenticated */}
                      {isAuthenticated && (
                        <>
                          <WorkspaceSelector
                            key={workspaceSelectorRefreshKey}
                            app_id="docreview"
                            selectedOrganizationId={selectedOrganizationId || undefined}
                            selectedWorkspaceId={selectedWorkspaceId || undefined}
                            onWorkspaceSelect={handleWorkspaceSelect}
                            refreshOnSelect={false}
                            debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                            isGlobalAdmin={isGlobalAdmin}
                            canCreateWorkspace={canCreateWorkspace}
                            onCreateWorkspace={handleCreateWorkspace}
                          />
                        </>
                      )}
                    </div>
                    {/* Profile Menu for tablet view */}
                    {isAuthenticated && (
                      <div className="ml-auto">
                        <ProfileMenu isGlobalAdmin={isGlobalAdmin} app_id="docreview" />
                      </div>
                    )}
                  </>
                ) : (
                  // Full desktop view - split between left and right sections
                  <>
                    <div className="flex items-center space-x-8">
                      {/* Show organization selector for global admins or users with multiple orgs */}
                      {isAuthenticated && showOrgSelector && (
                        <OrganizationSelector
                          app_id="docreview"
                          selectedOrganizationId={selectedOrganizationId || undefined}
                          onOrganizationSelect={selectOrganization}
                          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                        />
                      )}
                      {/* Only show workspace switcher when authenticated */}
                      {isAuthenticated && (
                        <>
                          <WorkspaceSelector
                            key={workspaceSelectorRefreshKey}
                            app_id="docreview"
                            selectedOrganizationId={selectedOrganizationId || undefined}
                            selectedWorkspaceId={selectedWorkspaceId || undefined}
                            onWorkspaceSelect={handleWorkspaceSelect}
                            refreshOnSelect={false}
                            debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                            isGlobalAdmin={isGlobalAdmin}
                            canCreateWorkspace={canCreateWorkspace}
                            onCreateWorkspace={handleCreateWorkspace}
                          />
                        </>
                      )}
                    </div>

                    {/* Profile Menu */}
                    {isAuthenticated && (
                      <div className="ml-auto">
                        <ProfileMenu isGlobalAdmin={isGlobalAdmin} app_id="docreview" />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Portal target for desktop/tablet tabs - DynamicTabs renders TabsList here via
                  React portal to keep tabs visually in header while maintaining same React context
                  as content. This ensures Radix generates matching aria-controls IDs for a11y. */}
              {!isMobile && !configLoading && location.pathname === '/' && (
                <div id="desktop-tabs-portal" className="py-4" />
              )}
            </div>

            {/* Mobile menu dropdown */}
            {isMobile && mobileMenuOpen && (
              <div className="border-t border-gray-200">
                <div className="pt-4 pb-3 space-y-3">
                  {/* Profile Menu at top of mobile menu */}
                  {isAuthenticated && (
                    <div className="px-4 pb-3 border-b border-gray-200">
                      <ProfileMenu isGlobalAdmin={isGlobalAdmin} app_id="docreview" />
                    </div>
                  )}
                  {/* Show organization selector for global admins or users with multiple orgs on mobile */}
                  {isAuthenticated && showOrgSelector && (
                    <div className="px-4">
                      <div className="text-xs text-gray-500 mb-1">Organization</div>
                      <OrganizationSelector
                        app_id="docreview"
                        selectedOrganizationId={selectedOrganizationId || undefined}
                        onOrganizationSelect={selectOrganization}
                        debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                      />
                    </div>
                  )}
                  {/* Only show workspace switcher when authenticated on mobile */}
                  {isAuthenticated && (
                    <div className="px-4">
                      <div className="text-xs text-gray-500 mb-1">Workspace</div>
                      <WorkspaceSelector
                        key={workspaceSelectorRefreshKey}
                        app_id="docreview"
                        selectedOrganizationId={selectedOrganizationId || undefined}
                        selectedWorkspaceId={selectedWorkspaceId || undefined}
                        onWorkspaceSelect={handleWorkspaceSelect}
                        refreshOnSelect={false}
                        debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
                        isGlobalAdmin={isGlobalAdmin}
                        canCreateWorkspace={canCreateWorkspace}
                        onCreateWorkspace={handleCreateWorkspace}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
        </nav>

          {/* Portal target for mobile tabs - same a11y pattern as desktop */}
          {isMobile && !configLoading && location.pathname === '/' && (
            <div className="bg-white border-b">
              <div id="mobile-tabs-portal" className="w-full px-2 py-3" />
            </div>
          )}

          <main className="flex-1 min-h-0 overflow-hidden">
            <div className="relative h-full overflow-auto">
              {/* A connector administrator without membership has no dashboard
                  in this workspace — their surface is Connectors. */}
              {currentWorkspace?.role === 'connector-admin'
                && location.pathname === '/' ? (
                <Navigate to="/workspace-connectors" replace />
              ) : configLoading && location.pathname === '/' ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-gray-500">Loading workspace configuration...</div>
                </div>
              ) : is403 && location.pathname === '/' ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-sm text-amber-600">
                    You do not have access to this workspace.
                    {accessibleWorkspaces.length > 0
                      ? <div className="mt-2 text-xs text-gray-500">Switching to an accessible workspace...</div>
                      : <div className="mt-2 text-xs text-gray-500">No accessible workspaces found. Contact your administrator.</div>
                    }
                  </div>
                </div>
              ) : configError && location.pathname === '/' ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-sm text-red-600">
                    Failed to load workspace configuration.
                    <div className="mt-2 text-xs text-gray-500">{configError.message}</div>
                  </div>
                </div>
              ) : location.pathname === '/' ? (
                // Dashboard route - render DynamicTabs with portal for tabs
                <DynamicTabs
                  uiLayout={layout || undefined}
                  userRole={userRole}
                  workspaceId={selectedWorkspaceId || ''}
                  organizationId={selectedOrganizationId || undefined}
                  workspaceType={currentWorkspace?.workspaceType}
                  renderMode="full"
                  activeTabId={activeTabId}
                  onTabChange={handleTabChange}
                  isMobile={isMobile}
                  tabsPortalId={isMobile ? 'mobile-tabs-portal' : 'desktop-tabs-portal'}
                />
              ) : (
                // Other routes - render via Outlet
                <Outlet />
              )}
            </div>
          </main>
      </div>
      </div>

      {/* Application selector modal for workspace creation */}
      {selectedOrganizationId && (
        <WorkspaceWizard
          app_id="docreview"
          open={appSelectorOpen}
          onOpenChange={setAppSelectorOpen}
          orgId={selectedOrganizationId}
          onWorkspaceCreated={handleWorkspaceCreated}
          debug={import.meta.env.VITE_WORKSPACES_DEBUG_LOGGING === 'true'}
        />
      )}

    </div>
    </SidebarProvider>
  );
}
